import { useReanalyzeAsset } from '@/features/assets/api/reanalyze-asset/reanalyze-asset.hook';
import { logError, logWarning } from '@/lib/log-error';
import { invalidateKeys, queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { GetBulkAssetsStatusResponse } from '@borradh-workspace/api-client/types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import {
  claimAnalysisRetry,
  releaseAnalysisRetry,
  selectPendingAssetIds,
  useAnalysisTrackerStore,
} from './analysis-tracker.store';
import {
  type SettledAnalysis,
  isPollingCancelled,
  pollAnalysisBatch,
  pollFailureLevel,
} from './poll-analysis-batch';

/** Delay before retrying after a failed poll, so an outage cannot spin. */
const ERROR_RETRY_MS = 30_000;

const fetchBulkStatus = (assetIds: string[]) =>
  apiClient.get<GetBulkAssetsStatusResponse>(
    `assets/bulk-status?assetIds=${encodeURIComponent(assetIds.join(','))}`
  );

/**
 * Runs the AI-analysis watch for the whole app.
 *
 * Mounted in the sibling `_authed` and `_onboarding` layouts, so tagging keeps
 * being tracked through onboarding, navigation, and closing the upload dialog.
 * Upload screens register asset ids with the tracker store and render from it;
 * none of them owns a polling loop any more.
 *
 * This is a REPORTING layer. The worker persists analysis status and the
 * resulting tags server-side, so if this component never ran at all the only
 * loss would be the live update and the toast.
 */
export function BackgroundAnalysisProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const { reanalyzeAssetAsync } = useReanalyzeAsset();

  // Deliberately mount-once. Tying this to a reactive `pendingCount` would
  // re-run the effect on every settle, and its cleanup would abort the poller
  // that just produced the settle. The store is read imperatively instead, and
  // a subscription starts a run whenever work appears.
  useEffect(() => {
    const controller = new AbortController();
    // Each effect lifetime owns its poller. A cancelled StrictMode run must
    // not block the replacement effect or clear its running flag later.
    let running = false;
    let consecutiveFailures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const run = () => {
      if (running || controller.signal.aborted) return;
      if (
        selectPendingAssetIds(useAnalysisTrackerStore.getState()).length === 0
      )
        return;

      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = undefined;
      }

      running = true;
      const completedLabels: string[] = [];
      const failedAssetIds: string[] = [];
      let failedCount = 0;
      let erroredOut = false;

      const applySettled = (settled: SettledAnalysis[]) => {
        const { tracked, settle } = useAnalysisTrackerStore.getState();

        for (const result of settled) {
          const label = tracked[result.assetId]?.label ?? 'video';
          settle(result.assetId, { status: result.status, tags: result.tags });
          invalidateKeys(
            queryClient,
            queryKeys.assets.analysis(result.assetId)
          );

          if (result.status === 'completed') completedLabels.push(label);
          else if (result.status === 'failed') {
            failedCount++;
            failedAssetIds.push(result.assetId);
          }
        }

        // The tags now live on the asset row, so any library listing is stale.
        invalidateKeys(queryClient, queryKeys.assets.all());
      };

      void pollAnalysisBatch({
        signal: controller.signal,
        getPendingAssetIds: () =>
          selectPendingAssetIds(useAnalysisTrackerStore.getState()),
        fetchStatus: fetchBulkStatus,
        onSettled: applySettled,
      })
        .then(() => {
          consecutiveFailures = 0;
          if (controller.signal.aborted) return;

          if (completedLabels.length === 1) {
            toast.success(`Tagging finished for ${completedLabels[0]}`);
          } else if (completedLabels.length > 1) {
            toast.success(
              `Tagging finished for ${completedLabels.length} videos`
            );
          }

          if (failedCount > 0) {
            // The worker already reported the real cause to Sentry; this is
            // the user-facing half only.
            toast.warning(
              failedCount === 1
                ? 'Tagging failed for 1 video. It is still in your library.'
                : `Tagging failed for ${failedCount} videos. They are still in your library.`,
              {
                action: {
                  label: 'Retry',
                  onClick: () => {
                    for (const assetId of failedAssetIds) {
                      if (!claimAnalysisRetry(assetId)) continue;
                      const label =
                        useAnalysisTrackerStore.getState().tracked[assetId]
                          ?.label ?? 'video';
                      void reanalyzeAssetAsync(assetId)
                        .then(() => {
                          const tracker = useAnalysisTrackerStore.getState();
                          tracker.untrack([assetId]);
                          tracker.track([{ assetId, label }]);
                        })
                        .catch(() => undefined)
                        .finally(() => {
                          releaseAnalysisRetry(assetId);
                        });
                    }
                  },
                },
              }
            );
          }
        })
        .catch((error) => {
          if (isPollingCancelled(error)) return;
          erroredOut = true;
          consecutiveFailures++;
          // The loop retries on its own, so one unanswered poll is not an
          // incident; see `pollFailureLevel`.
          if (pollFailureLevel(error, consecutiveFailures) === 'warning') {
            logWarning(
              'assets.backgroundAnalysis',
              'Analysis status poll got no response; retrying',
              { feature: 'assets', extra: { consecutiveFailures } }
            );
          } else {
            logError('assets.backgroundAnalysis', error, {
              feature: 'assets',
              extra: { consecutiveFailures },
            });
          }
          // Rows stay pending on a transient network failure — that is not an
          // analysis outcome.
        })
        .finally(() => {
          running = false;
          if (controller.signal.aborted) return;

          if (erroredOut) {
            // Restarting immediately would spin: the rows are still pending, so
            // `run()` would re-enter and fail again with no delay between
            // attempts. Back off instead, and let a store change pre-empt it.
            retryTimer = setTimeout(run, ERROR_RETRY_MS);
            return;
          }

          // Assets registered while this run was draining need a fresh run.
          run();
        });
    };

    const unsubscribe = useAnalysisTrackerStore.subscribe(run);
    run();

    return () => {
      controller.abort();
      unsubscribe();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [queryClient, reanalyzeAssetAsync]);

  return <>{children}</>;
}
